/**
 * Admin V3 — Operations / Live — top-level page
 *
 * Mounted at: /admin-v3/operations (see AdminV3.js)
 *
 * GOVERNANCE MEANING: Live Operations Dispatch View (read-only).
 * Mirror of the legacy LiveControlPanel (AdminControlTower.js:200-664) at the
 * same 5-second polling cadence. NO mutation surface — cancel/reassign/incident
 * controls are Phase 3+ scope.
 *
 * SCOPE FOR PHASE 2.5:
 *   - GET /admin/control-tower/live every 5 seconds via the new refetchInterval
 *     option on useAdminQuery (Phase 2.5 backward-compatible hook enhancement).
 *   - Server-side search via the endpoint's `search` query param (booking id /
 *     reference / phone / email).
 *   - 5-card KPI strip + prioritized booking table with anomaly badges.
 *   - Row click → /admin/bookings/:id (legacy V2 detail, Phase 2.2 precedent).
 *
 * EXPLICIT ZERO MUTATIONS:
 *   - No POST/PATCH/PUT/DELETE; no cancel button; no reassign button;
 *     no incident-report button; no admin overrides.
 *   - HTTP polling only (owner ruling #12 — no WebSocket / SSE / EventSource).
 */

import React, { useState, useEffect } from 'react';
import { Activity } from 'lucide-react';
import { SectionCard } from '../../shared/visualTokens';
import useAdminQuery from '../../shared/hooks/useAdminQuery';
import { t, DEFAULT_LOCALE } from '../../i18n';
import OperationsLiveSearchBar from './OperationsLiveSearchBar';
import OperationsLiveKpiStrip from './OperationsLiveKpiStrip';
import OperationsLiveTable from './OperationsLiveTable';

const SEARCH_DEBOUNCE_MS = 300;
const LIVE_REFETCH_INTERVAL_MS = 5000;  // mirror legacy AdminControlTower.js:259

const OperationsLivePage = () => {
  const locale = DEFAULT_LOCALE;

  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  useEffect(() => {
    const tid = setTimeout(() => setDebouncedSearch(search), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(tid);
  }, [search]);

  // Backend supports server-side search via ?search=...
  const queryParams = {};
  if (debouncedSearch) queryParams.search = debouncedSearch;

  // 5-second polling for live dispatch (owner ruling #1 — mirror legacy parity).
  // useAdminQuery.refetchInterval enhancement is shipped in Phase 2.5.
  const { data, loading, error, refetch } = useAdminQuery(
    '/admin/control-tower/live',
    queryParams,
    { refetchInterval: LIVE_REFETCH_INTERVAL_MS }
  );

  // The /live endpoint returns a structured payload (NOT a bare array).
  const payload   = data && typeof data === 'object' ? data : {};
  const bookings  = Array.isArray(payload.bookings)  ? payload.bookings  : [];
  const anomalies = Array.isArray(payload.anomalies) ? payload.anomalies : [];

  // KPI values from the same single fetch (no extra round trips per owner ruling #6).
  const kpis = {
    activeBookings:  payload.active_bookings  ?? bookings.length,
    anomalyCount:    anomalies.length,
    onlineDrivers:   payload.online_drivers   ?? 0,
    pendingPayouts:  payload.pending_payouts  ?? 0,
    openIncidents:   payload.open_incidents   ?? 0,
  };

  // Map anomaly entries to booking_id for O(1) per-row badge lookup.
  const anomalyByBookingId = {};
  anomalies.forEach((a) => {
    if (!a?.booking_id) return;
    if (!anomalyByBookingId[a.booking_id]) anomalyByBookingId[a.booking_id] = [];
    anomalyByBookingId[a.booking_id].push(a);
  });

  const isFilterActive = Boolean(debouncedSearch);

  // Skeleton ONLY during the very first fetch (data === null). Subsequent polls
  // keep last-good data visible so the dispatch board never flashes empty.
  const showSkeleton = loading && !data;

  return (
    <SectionCard
      title={t('operations.live.title', locale)}
      icon={Activity}
      iconClass="bg-rose-100 text-rose-600"
    >
      <div className="space-y-4">
        <OperationsLiveSearchBar
          search={search}
          onSearchChange={setSearch}
          resultsCount={bookings.length}
          isFilterActive={isFilterActive}
          loading={loading}
          locale={locale}
        />

        <OperationsLiveKpiStrip
          kpis={kpis}
          loading={showSkeleton}
          locale={locale}
        />

        <OperationsLiveTable
          bookings={bookings}
          anomalyByBookingId={anomalyByBookingId}
          loading={showSkeleton}
          error={error}
          isFilterActive={isFilterActive}
          onRetry={refetch}
          locale={locale}
        />
      </div>
    </SectionCard>
  );
};

export default OperationsLivePage;
